
export interface BreakpointConfig {
  xs: number;
  sm: number;
  md: number;
  lg: number;
  xl: number;
  xxl: number;
}

export const DEFAULT_BREAKPOINTS: BreakpointConfig = {
  xs: 0,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1400
};

export type BreakpointKey = keyof BreakpointConfig;
export type ResponsiveValue<T> = T | Partial<Record<BreakpointKey, T>>;

export class ResponsiveService {
  private static instance: ResponsiveService;
  private breakpoints: BreakpointConfig;
  private listeners: Set<(breakpoint: BreakpointKey) => void> = new Set();
  private currentBreakpoint: BreakpointKey = 'xl';

  private constructor(breakpoints?: BreakpointConfig) {
    this.breakpoints = breakpoints || DEFAULT_BREAKPOINTS;
    this.init();
  }

  public static getInstance(breakpoints?: BreakpointConfig): ResponsiveService {
    if (!ResponsiveService.instance) {
      ResponsiveService.instance = new ResponsiveService(breakpoints);
    }
    return ResponsiveService.instance;
  }

  private init(): void {
    if (typeof window !== 'undefined') {
      this.updateCurrentBreakpoint();
      window.addEventListener('resize', this.handleResize);
    }
  }

  private handleResize = (): void => {
    this.updateCurrentBreakpoint();
  };

  private updateCurrentBreakpoint(): void {
    const width = window.innerWidth;
    let newBreakpoint: BreakpointKey = 'xs';

    const sortedBreakpoints = Object.entries(this.breakpoints)
      .sort(([, a], [, b]) => b - a);

    for (const [key, value] of sortedBreakpoints) {
      if (width >= value) {
        newBreakpoint = key as BreakpointKey;
        break;
      }
    }

    if (newBreakpoint !== this.currentBreakpoint) {
      const oldBreakpoint = this.currentBreakpoint;
      this.currentBreakpoint = newBreakpoint;
      this.notifyListeners(newBreakpoint, oldBreakpoint);
    }
  }

  private notifyListeners(newBreakpoint: BreakpointKey, oldBreakpoint?: BreakpointKey): void {
    this.listeners.forEach(listener => listener(newBreakpoint));
  }


  public getCurrentBreakpoint(): BreakpointKey {
    return this.currentBreakpoint;
  }

  
  public isBreakpointUp(breakpoint: BreakpointKey): boolean {
    const currentWidth = typeof window !== 'undefined' ? window.innerWidth : 1200;
    return currentWidth >= this.breakpoints[breakpoint];
  }


  public isBreakpointDown(breakpoint: BreakpointKey): boolean {
    const currentWidth = typeof window !== 'undefined' ? window.innerWidth : 1200;
    const nextBreakpoint = this.getNextBreakpoint(breakpoint);
    return nextBreakpoint ? currentWidth < this.breakpoints[nextBreakpoint] : true;
  }

 
  public isBreakpointBetween(min: BreakpointKey, max: BreakpointKey): boolean {
    return this.isBreakpointUp(min) && this.isBreakpointDown(max);
  }

  
  public isMobile(): boolean {
    return this.isBreakpointDown('md');
  }

  public isTablet(): boolean {
    return this.isBreakpointBetween('md', 'lg');
  }

  public isDesktop(): boolean {
    return this.isBreakpointUp('lg');
  }

  public resolveResponsiveValue<T>(value: ResponsiveValue<T>): T {
    if (typeof value !== 'object' || value === null) {
      return value as T;
    }

    const responsiveValue = value as Partial<Record<BreakpointKey, T>>;
    
    // Buscar el valor para el breakpoint actual o el más cercano menor
    const breakpointOrder: BreakpointKey[] = ['xxl', 'xl', 'lg', 'md', 'sm', 'xs'];
    const currentIndex = breakpointOrder.indexOf(this.currentBreakpoint);
    
    for (let i = currentIndex; i < breakpointOrder.length; i++) {
      const breakpoint = breakpointOrder[i];
      if (responsiveValue[breakpoint] !== undefined) {
        return responsiveValue[breakpoint]!;
      }
    }

    // Si no se encuentra nada, devolver el primer valor disponible
    const firstKey = Object.keys(responsiveValue)[0] as BreakpointKey;
    return responsiveValue[firstKey] as T;
  }

  public subscribe(listener: (breakpoint: BreakpointKey) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  public getResponsiveClasses<T extends string>(
    prefix: string, 
    values: ResponsiveValue<T>
  ): string {
    if (typeof values !== 'object' || values === null) {
      return `${prefix}-${values}`;
    }

    const classes: string[] = [];
    const responsiveValue = values as Partial<Record<BreakpointKey, T>>;

    Object.entries(responsiveValue).forEach(([breakpoint, value]) => {
      if (value !== undefined) {
        const suffix = breakpoint === 'xs' ? '' : `-${breakpoint}`;
        classes.push(`${prefix}${suffix}-${value}`);
      }
    });

    return classes.join(' ');
  }

  private getNextBreakpoint(breakpoint: BreakpointKey): BreakpointKey | null {
    const order: BreakpointKey[] = ['xs', 'sm', 'md', 'lg', 'xl', 'xxl'];
    const currentIndex = order.indexOf(breakpoint);
    return currentIndex < order.length - 1 ? order[currentIndex + 1] : null;
  }

  public destroy(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.handleResize);
    }
    this.listeners.clear();
  }
}

export const responsiveService = ResponsiveService.getInstance();

export interface SpacingConfig {
  xs: string;
  sm: string;
  md: string;
  lg: string;
  xl: string;
}

export const SPACING_VALUES: SpacingConfig = {
  xs: '0.5rem',
  sm: '1rem', 
  md: '2rem',
  lg: '3rem',
  xl: '4rem'
};

/**
 * Container responsive configurations
 */
export interface ContainerConfig {
  maxWidth: ResponsiveValue<string>;
  padding: ResponsiveValue<string>;
  margin?: ResponsiveValue<string>;
}

export const CONTAINER_CONFIGS = {
  default: {
    maxWidth: {
      xs: '100%',
      sm: '540px',
      md: '720px', 
      lg: '960px',
      xl: '1140px',
      xxl: '1320px'
    },
    padding: {
      xs: '1rem',
      sm: '1.5rem',
      md: '2rem'
    }
  } as ContainerConfig,
  
  fluid: {
    maxWidth: '100%',
    padding: {
      xs: '1rem',
      sm: '2rem'
    }
  } as ContainerConfig,
  
  narrow: {
    maxWidth: {
      xs: '100%',
      sm: '480px',
      md: '600px',
      lg: '720px',
      xl: '840px'
    },
    padding: {
      xs: '1rem',
      md: '2rem'
    }
  } as ContainerConfig
};

/**
 * Utilitarios para centrado vertical
 */
export interface CenteringConfig {
  method: 'flexbox' | 'grid' | 'position';
  minHeight?: ResponsiveValue<string>;
  justifyContent?: 'center' | 'flex-start' | 'flex-end';
  alignItems?: 'center' | 'flex-start' | 'flex-end';
}

export const CENTERING_PRESETS = {
  fullHeight: {
    method: 'flexbox' as const,
    minHeight: '100vh',
    justifyContent: 'center',
    alignItems: 'center'
  },
  
  sectionHeight: {
    method: 'flexbox' as const,
    minHeight: {
      xs: '60vh',
      md: '80vh'
    },
    justifyContent: 'center', 
    alignItems: 'center'
  },
  
  autoHeight: {
    method: 'flexbox' as const,
    minHeight: 'auto',
    justifyContent: 'center',
    alignItems: 'center'
  }
} as const;